import { ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons'
import { useIsNarrow } from '@/hooks/useIsNarrow'
import { breakpoints, iconSizes } from '@/theme/tokens'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
	LegendRow,
	clampLegendPage,
	legendPageCount,
	legendPageOfRow,
	legendPageRows,
	legendPageSize,
} from '../core/destinations'
import {
	ColorBar,
	LEGEND_PAGER_HEIGHT,
	LEGEND_ROW_GAP,
	LEGEND_ROW_HEIGHT,
	LegendBlock,
	LegendBody,
	LegendList,
	LegendRowButton,
	LegendRowItem,
	Pager,
	PagerButton,
	PagerDot,
	PagerDots,
	RowDates,
	RowDetail,
	RowName,
	RowText,
} from './DestinationLegend.styles'
import { TestIds } from '@/testIds'

export interface DestinationLegendProps {
	rows: LegendRow[]
	/** Zero-based selected trip day: picking a day turns the legend to its destination's page. */
	selectedDay: number
	/** Where the selected day's destination sits in `rows`; -1 when no destination covers the day. */
	activeIndex: number
	/** Picking a destination goes to its first day. */
	onSelectDay: (dayIndex: number) => void
}

const METRICS = { rowHeight: LEGEND_ROW_HEIGHT, rowGap: LEGEND_ROW_GAP, pagerHeight: LEGEND_PAGER_HEIGHT }
/** Rows per page where the legend is not held to a height: the stacked card, or not laid out yet. */
const FLOWING_PAGE_SIZE = 3

/**
 * The trip's destinations, each in the colour its calendar days wear. Under
 * the calendar it fills what the column has left and no more:
 * destinations that do not fit go onto further pages, and the selected day's
 * destination is always turned to and faintly highlighted. Picking a row goes
 * to that destination's first day.
 */
const DestinationLegend: React.FC<DestinationLegendProps> = ({ rows, selectedDay, activeIndex, onSelectDay }) => {
	const stacked = useIsNarrow(breakpoints.tabletWide)
	const bodyRef = useRef<HTMLDivElement>(null)
	const [bodyHeight, setBodyHeight] = useState(0)
	// No destinations, no legend: the body only exists to be measured once there are rows.
	const empty = rows.length === 0

	useLayoutEffect(() => {
		const body = bodyRef.current
		if (!body || stacked) return
		const measure = () => setBodyHeight(body.clientHeight)
		measure()
		const observer = new ResizeObserver(measure)
		observer.observe(body)
		return () => observer.disconnect()
	}, [stacked, empty])

	const pageSize = legendPageSize(stacked ? 0 : bodyHeight, rows.length, METRICS, FLOWING_PAGE_SIZE)
	const pageCount = legendPageCount(rows.length, pageSize)

	const [page, setPage] = useState(() => legendPageOfRow(activeIndex, pageSize))
	// A picked day always wins over wherever the reader had paged to, even a
	// second day of the same destination; so does a re-flow that moves the row.
	useEffect(() => {
		if (activeIndex >= 0) setPage(legendPageOfRow(activeIndex, pageSize))
	}, [selectedDay, activeIndex, pageSize])

	const currentPage = clampLegendPage(page, rows.length, pageSize)
	const firstRow = currentPage * pageSize
	const visibleRows = legendPageRows(rows, currentPage, pageSize)

	if (empty) return null

	return (
		<LegendBlock data-testid={TestIds.itinerary.destinationLegend}>
			<LegendBody ref={bodyRef}>
				<LegendList>
					{visibleRows.map((row, offset) => {
						const active = firstRow + offset === activeIndex
						return (
							<LegendRowItem key={row.id} aria-current={active ? 'true' : undefined}>
								<LegendRowButton type="button" $selected={active} onClick={() => onSelectDay(row.startDayIndex)}>
									<ColorBar aria-hidden="true" $colorIndex={row.colorIndex} />
									<RowText>
										<RowName>{row.name}</RowName>
										{row.detail && <RowDetail>{row.detail}</RowDetail>}
									</RowText>
									<RowDates>{row.dateRange}</RowDates>
								</LegendRowButton>
							</LegendRowItem>
						)
					})}
				</LegendList>
				{pageCount > 1 && (
					<Pager aria-label={`Destinations, page ${currentPage + 1} of ${pageCount}`}>
						<PagerButton
							type="button"
							aria-label="Previous destinations"
							disabled={currentPage === 0}
							onClick={() => setPage(currentPage - 1)}
						>
							<ChevronLeftIcon sx={{ fontSize: iconSizes.sm }} />
						</PagerButton>
						<PagerDots aria-hidden="true">
							{Array.from({ length: pageCount }, (_, index) => (
								<PagerDot key={index} $current={index === currentPage} />
							))}
						</PagerDots>
						<PagerButton
							type="button"
							aria-label="Next destinations"
							disabled={currentPage === pageCount - 1}
							onClick={() => setPage(currentPage + 1)}
						>
							<ChevronRightIcon sx={{ fontSize: iconSizes.sm }} />
						</PagerButton>
					</Pager>
				)}
			</LegendBody>
		</LegendBlock>
	)
}

export default DestinationLegend
